import { type ReactNode, useState } from "react";
import type { TFunction } from "i18next";
import { Film, Image as ImageIcon } from "lucide-react";
import { type PostMedia, type RichText, type TextRun, pictureSrc } from "@/lib/aggregator";
import { cn } from "@/lib/cn";

/** A titled block of a settings form. */
export function Section({
  title,
  hint,
  children,
  aside,
}: {
  title: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-fg-primary">{title}</h3>
          {hint && <p className="mt-0.5 text-xs leading-snug text-fg-muted">{hint}</p>}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

function runClass(run: TextRun): string {
  return cn(
    run.styles.includes("b") && "font-semibold",
    run.styles.includes("i") && "italic",
    run.styles.includes("u") && "underline",
    run.styles.includes("s") && "line-through",
    run.styles.includes("code") && "rounded bg-bg-elevated px-1 font-mono text-[0.92em]",
    run.styles.includes("spoiler") && "rounded bg-fg-muted/25 px-0.5",
  );
}

function Run({ run }: { run: TextRun }) {
  const className = runClass(run) || undefined;
  if (run.href) {
    return (
      <a
        href={run.href}
        target="_blank"
        rel="noreferrer noopener"
        className={cn("text-accent underline-offset-2 hover:underline", className)}
      >
        {run.text}
      </a>
    );
  }
  return className ? <span className={className}>{run.text}</span> : <>{run.text}</>;
}

/**
 * A post's text drawn from its pieces — never as markup put into the page, so
 * nothing a source wrote can run in the operator's browser.
 */
export function RichTextView({
  text,
  className,
  clamp = false,
}: {
  text: RichText;
  className?: string;
  /** Show the first lines only, until asked for more. */
  clamp?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // Consecutive quoted pieces are one quote.
  const blocks: { quoted: boolean; runs: TextRun[] }[] = [];
  for (const run of text.runs) {
    const quoted = run.styles.includes("quote");
    const last = blocks[blocks.length - 1];
    if (last && last.quoted === quoted) last.runs.push(run);
    else blocks.push({ quoted, runs: [run] });
  }
  const long = clamp && (text.markup.length > 420 || text.markup.split("\n").length > 7);
  return (
    <div className={className}>
      <div
        className={cn(
          "whitespace-pre-wrap break-words text-sm leading-relaxed text-fg-primary",
          long && !open && "line-clamp-6",
        )}
      >
        {blocks.map((block, n) =>
          block.quoted ? (
            <blockquote key={n} className="my-1 border-l-2 border-accent/60 pl-3 text-fg-secondary">
              {block.runs.map((run, i) => (
                <Run key={i} run={run} />
              ))}
            </blockquote>
          ) : (
            block.runs.map((run, i) => <Run key={`${n}-${i}`} run={run} />)
          ),
        )}
      </div>
      {long && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-1 text-xs font-medium text-accent hover:underline"
        >
          {open ? "↑" : "…"}
        </button>
      )}
    </div>
  );
}

function Thumb({ item, label }: { item: PostMedia; label: string }) {
  const [broken, setBroken] = useState(false);
  const src = item.kind === "photo" ? item.url : item.thumb;
  const Icon = item.kind === "photo" ? ImageIcon : Film;
  // A picture kept by us has no address of its own: it opens through this server.
  const href = item.url.startsWith("kept:") ? pictureSrc(item.url) : item.url;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      title={label}
      className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-bg-elevated text-fg-muted"
    >
      {src && !broken ? (
        <img
          src={pictureSrc(src)}
          alt={label}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setBroken(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <Icon size={20} strokeWidth={1.75} aria-hidden="true" />
      )}
      {item.kind !== "photo" && (
        <span className="absolute bottom-0.5 right-0.5 rounded bg-bg-overlay/80 px-1 text-[10px] leading-tight text-fg-on-accent">
          {label}
        </span>
      )}
    </a>
  );
}

/** A post's pictures and clips, small; and what the page did not hand over. */
export function MediaStrip({
  media,
  missing,
  t,
}: {
  media: PostMedia[];
  missing: string[];
  t: TFunction;
}) {
  if (media.length === 0 && missing.length === 0) return null;
  return (
    <div className="space-y-1.5">
      {media.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {media.map((item, n) => (
            <Thumb key={n} item={item} label={t(`aggregator.media.${item.kind}`)} />
          ))}
        </div>
      )}
      {missing.length > 0 && (
        <p className="text-xs text-warning">
          {t("aggregator.media.missing", {
            what: missing.map((kind) => t(`aggregator.media.missing_kind.${kind}`, kind)).join(", "),
          })}
        </p>
      )}
    </div>
  );
}
